import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, FileText, Globe2, ArrowUpRight, AlertCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import { REGULATIONS, COMPLIANCE_POINTS } from "../lib/constants";

export default function RegulationsPage() {
  return (
    <>
      <PageHero
        eyebrow="Regulations"
        breadcrumb={[{ label: "Regulations" }]}
        title={<>Compliance is <span className="text-gradient-cyan italic">non-negotiable.</span></>}
        lede="We're a technology vendor, not a broker — but every workflow we ship is built to fit the licensing regime our clients operate under."
      />

      {/* Disclaimer banner */}
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="p-6 rounded-2xl border border-[#00C2FF]/30 bg-[#00C2FF]/5 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-[#00C2FF] shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300 leading-relaxed">
              <strong className="text-white">Important:</strong> FX Broker Xpert does not hold a
              brokerage license, does not solicit deposits, and does not provide trading or
              investment advice. The regulators below are jurisdictions we've helped clients
              integrate with — not licenses we hold ourselves.
            </div>
          </div>
        </div>
      </section>

      {/* Regulator grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-10 h-px bg-[#00C2FF]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
              / Jurisdictions we've integrated with
            </span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {REGULATIONS.map((r, i) => (
              <motion.div
                key={r.code}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`regulator-${r.code.toLowerCase()}`}
                className="p-6 rounded-2xl theme-panel border hover:border-[#00C2FF]/40 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <ShieldCheck className="w-6 h-6 text-[#00C2FF]" />
                  <span className="font-mono text-[10px] text-gray-500 tracking-widest">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="font-display text-2xl font-extrabold text-gradient-cyan">{r.code}</div>
                <div className="text-sm text-white mt-1">{r.country}</div>
                <div className="text-xs text-gray-500 mt-2 font-mono uppercase tracking-wider">{r.focus}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance principles */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mb-10">
            Our compliance <span className="text-gradient-cyan italic">principles.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {COMPLIANCE_POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-6 rounded-2xl theme-panel border"
              >
                <FileText className="w-6 h-6 text-[#00C2FF] mb-4" />
                <h3 className="font-display text-lg font-semibold">{p.title}</h3>
                <p className="text-sm text-gray-400 mt-2 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-white/10 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <Globe2 className="w-8 h-8 text-[#00C2FF] mx-auto mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Need a specific jurisdiction?
          </h2>
          <p className="text-gray-400 mt-3">
            Tell us which regulator you're aligning with — we'll map the integrations needed.
          </p>
          <Link to="/contact" className="btn-cyan inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full font-semibold">
            Get in touch <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
